import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface FeatureFlags {
  experimental_image_viewer: boolean;
  experimental_bulk_upload: boolean;
  debug_show_session_info: boolean;
  enable_dark_mode: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  experimental_image_viewer: false,
  experimental_bulk_upload: false,
  debug_show_session_info: false,
  enable_dark_mode: false,
};

const STORAGE_KEY = "facturacion:feature_flags";

export interface FeatureFlagsContextValue {
  flags: FeatureFlags;
  isEnabled: (key: keyof FeatureFlags) => boolean;
  setFlag: (key: keyof FeatureFlags, value: boolean) => void;
  reset: () => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);
export { FeatureFlagsContext };

const loadFromStorage = (): FeatureFlags => {
  if (typeof window === "undefined") return DEFAULT_FLAGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FLAGS;
    const parsed = JSON.parse(raw) as Partial<FeatureFlags>;
    return { ...DEFAULT_FLAGS, ...parsed };
  } catch {
    return DEFAULT_FLAGS;
  }
};

const saveToStorage = (flags: FeatureFlags): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch {
    // ignore quota errors
  }
};

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const [flags, setFlags] = useState<FeatureFlags>(loadFromStorage);

  useEffect(() => {
    saveToStorage(flags);
  }, [flags]);

  const isEnabled = useCallback(
    (key: keyof FeatureFlags): boolean => Boolean(flags[key]),
    [flags],
  );

  const setFlag = useCallback((key: keyof FeatureFlags, value: boolean): void => {
    setFlags((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback((): void => {
    setFlags(DEFAULT_FLAGS);
  }, []);

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({ flags, isEnabled, setFlag, reset }),
    [flags, isEnabled, setFlag, reset],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextValue => {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error("useFeatureFlags debe usarse dentro de FeatureFlagsProvider");
  }
  return ctx;
};